import React, { useState, useEffect } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  // Funções de CONFIRMATION
  createConfirmationSequence,
  getConfirmationSequence,
  updateConfirmationSequence,

  // Funções de CONFIRMATION SCHEDULE
  getConfirmationScheduleConfig,
  createConfirmationScheduleConfig,
  updateConfirmationScheduleConfig,
  deleteConfirmationScheduleConfig,

  // Funções de upload e delete de arquivos
  uploadFile,
  getFileUrl,
  getClinicWhatsAppProviders,
  listWhatsAppTemplates,
  WhatsAppProviders,
  WhatsAppTemplate,
} from '../services/api.ts';

import {
  Trash2,
  Edit2,
  Plus,
  Calendar,
  Clock,
  MessageCircle,
  Check,
  X,
  Image,
  Video,
  Music,
  Type,
  File,
  AlertCircle,
  Save,
  GripVertical,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  MapPin,
} from 'lucide-react';
import {
  analyzeTemplateSupport,
  buildTemplateMessageConfig,
  getTemplateVariableOption,
  getTemplateVariableOptions,
  isTemplateSupportedForAutomation,
  parseStoredTemplateMessage,
  serializeTemplateMessage,
  TemplateMessageConfig,
} from '../utils/whatsappTemplateVariables.ts';

// ------------------------------------------------------------------
// 1) Tipos LOCAIS (React state) agora com "id?" para Steps/Mensagens
// ------------------------------------------------------------------
interface MensagemLocal {
  id?: number;  // <--- agora podemos ter ID
  client_key: string;
  position: number;
  type: 'text' | 'image' | 'audio' | 'video' | 'template' | 'location';
  content: string | File | any;
}

interface PassoLocal {
  id?: number;  // <--- idem, pode ter ID
  client_key: string;
  step_number: number;
  send_before: number;                         // front -> "antes"
  send_before_unit: 'days' | 'hours' | 'minutes';
  send_at_time?: string | null;
  messages: MensagemLocal[];
}

// ------------------------------------------------------------------
// 2) Tipos para API (com "id?" também), mas "content" sempre string
// ------------------------------------------------------------------
interface MensagemAPI {
  id?: number;
  type: 'text' | 'image' | 'audio' | 'video' | 'template' | 'location';
  content: string;
  position: number;
}

interface PassoAPI {
  id?: number;
  step_number: number;
  send_after: number;
  send_after_unit: 'days' | 'hours' | 'minutes';
  send_at_time?: string | null;
  messages: MensagemAPI[];
}

interface LocalizacaoMensagem {
  latitude: string;
  longitude: string;
  name: string;
  address: string;
}

// ------------------------------------------------------------------
// Tipos para Schedule, inalterados
// ------------------------------------------------------------------
interface ScheduleDay {
  enabled: boolean;
  start: string;
  end: string;
}

interface ScheduleData {
  [key: string]: ScheduleDay; 
}

// ------------------------------------------------------------------
// Constantes
// ------------------------------------------------------------------
const MAX_PASSOS = 10;

const LIMITE_ARQUIVOS = {
  image: 2 * 1024 * 1024, // 2 MB
  audio: 5 * 1024 * 1024, // 5 MB
  video: 5 * 1024 * 1024, // 5 MB
};

const daysOfWeek = [
  { label: 'Segunda', key: 'monday' },
  { label: 'Terça', key: 'tuesday' },
  { label: 'Quarta', key: 'wednesday' },
  { label: 'Quinta', key: 'thursday' },
  { label: 'Sexta', key: 'friday' },
  { label: 'Sábado', key: 'saturday' },
  { label: 'Domingo', key: 'sunday' },
];

const timeUnits = [
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' },
  { value: 'days', label: 'Dias' }
];

const messageTypes = [
  { value: 'text', label: 'Texto', icon: Type },
  { value: 'template', label: 'Template Meta', icon: MessageCircle },
  { value: 'image', label: 'Imagem', icon: Image },
  { value: 'audio', label: 'Áudio', icon: Music },
  { value: 'video', label: 'Vídeo', icon: Video },
  { value: 'location', label: 'Localização', icon: MapPin },
];

const normalizeLocationMessage = (content: unknown): LocalizacaoMensagem => {
  let value = content;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      value = {};
    }
  }
  const location = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    latitude: location.latitude == null ? '' : String(location.latitude),
    longitude: location.longitude == null ? '' : String(location.longitude),
    name: typeof location.name === 'string' ? location.name : '',
    address: typeof location.address === 'string' ? location.address : '',
  };
};

const locationMessageForApi = (content: unknown, stepNumber: number): string => {
  const location = normalizeLocationMessage(content);
  if (!location.latitude.trim() || !location.longitude.trim()) {
    throw new Error(`Informe latitude e longitude na localização do passo ${stepNumber}.`);
  }
  const latitude = Number(location.latitude);
  const longitude = Number(location.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)
    || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    throw new Error(`Latitude ou longitude inválida na localização do passo ${stepNumber}.`);
  }
  if (location.name.length > 255 || location.address.length > 255) {
    throw new Error(`Nome e endereço da localização devem ter até 255 caracteres.`);
  }
  return JSON.stringify({
    latitude,
    longitude,
    name: location.name.trim(),
    address: location.address.trim(),
  });
};

const makeClientKey = (prefix: 'step' | 'message', id?: number): string => {
  if (id) return `${prefix}-id-${id}`;
  const randomPart = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-new-${randomPart}`;
};

export const reorderItems = <T,>(items: T[], from: number, to: number): T[] => {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const renumberSteps = (steps: PassoLocal[]): PassoLocal[] =>
  steps.map((step, stepIndex) => ({
    ...step,
    step_number: stepIndex + 1,
    messages: step.messages.map((message, messageIndex) => ({
      ...message,
      position: messageIndex,
    })),
  }));

export const removeStepByKey = (steps: PassoLocal[], stepKey: string): PassoLocal[] =>
  renumberSteps(steps.filter((step) => step.client_key !== stepKey));

const timingInMinutes = (step: PassoLocal): number => {
  const multiplier = { minutes: 1, hours: 60, days: 1440 };
  return step.send_before * multiplier[step.send_before_unit];
};

const formatStepTiming = (step: PassoLocal): string => {
  const unit = step.send_before_unit === 'days'
    ? (step.send_before === 1 ? 'dia' : 'dias')
    : step.send_before_unit === 'hours'
      ? (step.send_before === 1 ? 'hora' : 'horas')
      : (step.send_before === 1 ? 'minuto' : 'minutos');
  if (step.send_before_unit === 'days' && step.send_at_time) {
    return step.send_before === 0
      ? `No dia da consulta · ${step.send_at_time}`
      : `${step.send_before} ${unit} antes · ${step.send_at_time}`;
  }
  return `${step.send_before} ${unit} antes`;
};

export const validateStepOrder = (steps: PassoLocal[]): string | null => {
  for (const [index, step] of steps.entries()) {
    const hasFixedClock = step.send_before_unit === 'days' && !!step.send_at_time;
    if (!Number.isInteger(step.send_before) || step.send_before < 0
      || (step.send_before === 0 && !hasFixedClock)) {
      return `Defina um período válido para o passo ${index + 1}. Para enviar no dia da consulta, escolha também um horário.`;
    }
    if (step.send_at_time && step.send_before_unit !== 'days') {
      return `O passo ${index + 1} só aceita horário fixo quando a unidade for dias.`;
    }
    if (step.send_at_time && !/^([01]\d|2[0-3]):[0-5]\d$/.test(step.send_at_time)) {
      return `Defina um horário válido para o passo ${index + 1}.`;
    }
  }

  for (let index = 1; index < steps.length; index += 1) {
    const previous = steps[index - 1];
    const current = steps[index];
    const previousFixed = previous.send_before_unit === 'days' && !!previous.send_at_time;
    const currentFixed = current.send_before_unit === 'days' && !!current.send_at_time;
    let invalid = false;

    if (previousFixed && currentFixed) {
      invalid = current.send_before > previous.send_before
        || (current.send_before === previous.send_before
          && (current.send_at_time || '') < (previous.send_at_time || ''));
    } else if (!previousFixed && !currentFixed) {
      invalid = timingInMinutes(current) > timingInMinutes(previous);
    }

    if (invalid) {
      return `O passo ${index + 1} está mais distante da consulta que o passo ${index}. Mova-o para cima ou ajuste o tempo.`;
    }
  }
  return null;
};

export const shouldLoadConfirmationTemplates = (
  providers: Pick<WhatsAppProviders, 'has_official'>,
): boolean => providers.has_official;

export const getScheduleRangePercentages = (
  start: string,
  end: string,
): { left: number; width: number } => {
  const toMinutes = (value: string) => {
    const [hours = 0, minutes = 0] = value.split(':').map(Number);
    return Math.min(1440, Math.max(0, (hours * 60) + minutes));
  };
  const startMinutes = toMinutes(start);
  const endMinutes = Math.max(startMinutes, toMinutes(end));

  return {
    left: (startMinutes / 1440) * 100,
    width: ((endMinutes - startMinutes) / 1440) * 100,
  };
};

// -------------------------------------------------------------
// Função para verificar se é um arquivo
// -------------------------------------------------------------
function isFile(value: any): boolean {
  return (
    typeof value === 'object' && 
    value !== null && 
    'name' in value && 
    'size' in value && 
    'type' in value
  );
}

// -------------------------------------------------------------
// Componente de preview de arquivo
// -------------------------------------------------------------
const PreviewArquivo = ({ tipo, conteudo }: { tipo: string; conteudo: string | File | any }) => {
  const { isDark } = useTheme();

  if (isFile(conteudo)) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-md my-2">
        <p className="text-sm text-amber-700 flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          Arquivo pendente de upload. Será enviado ao salvar as configurações.
        </p>
      </div>
    );
  }

  if (typeof conteudo !== 'string') {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-md my-2">
        <p className="text-sm text-red-700 flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          Formato de arquivo inválido
        </p>
      </div>
    );
  }

  const clinicId = Number(localStorage.getItem('clinic_id'));
  const clientId = Number(localStorage.getItem('client_id'));
  const fileName = conteudo.split('/').pop() || '';
  const url = getFileUrl(clinicId, clientId, fileName);

  if (tipo === 'image') {
    return (
      <div className="mt-2 rounded-lg overflow-hidden">
        <img
          src={url}
          alt={`Preview da imagem ${fileName}`}
          className="max-h-48 w-full rounded-lg object-contain sm:max-w-md"
        />
      </div>
    );
  }
  if (tipo === 'audio') {
    return (
      <div className={`mt-2 rounded-lg p-3 sm:max-w-xl ${isDark ? 'bg-gray-800' : 'bg-slate-100'}`}>
        <audio controls className="w-full">
          <source src={url} type="audio/mpeg" />
          Seu navegador não suporta o elemento de áudio.
        </audio>
      </div>
    );
  }
  if (tipo === 'video') {
    return (
      <div className="mt-2 rounded-lg overflow-hidden">
        <video controls className="max-h-48 w-full rounded-lg bg-black sm:max-w-md">
          <source src={url} type="video/mp4" />
          Seu navegador não suporta o elemento de vídeo.
        </video>
      </div>
    );
  }
  return null;
};

// Timeline indicator component
const TimelineIndicator = ({ passos, onEditStep, onAddStep }: { 
  passos: PassoLocal[], 
  onEditStep: (stepKey: string) => void,
  onAddStep: () => void
}) => {
  const { isDark } = useTheme();

  return (
    <div className={`overflow-hidden rounded-2xl border ${
      isDark ? 'border-gray-700 bg-[#111827]/60' : 'border-gray-200 bg-gray-50/70'
    }`}>
      <div className={`flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
        isDark ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <div>
          <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            Resumo da jornada
          </h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Ordem dos contatos antes da consulta.
          </p>
        </div>
        <span className="w-fit rounded-full bg-sminy-primary/10 px-2.5 py-1 text-xs font-medium text-sminy-primary">
          {passos.length} {passos.length === 1 ? 'passo' : 'passos'}
        </span>
      </div>

      <div className="relative space-y-3 p-4">
        <div className={`absolute bottom-8 left-[31px] top-8 w-px ${isDark ? 'bg-gray-700' : 'bg-sminy-primary/20'}`} />
        {passos.map((step) => {
          return (
            <div key={step.client_key} className="relative flex items-center gap-3">
              <div className="z-10 grid h-8 w-8 flex-shrink-0 place-items-center rounded-xl bg-sminy-primary text-xs font-semibold text-white shadow-sm">
                {step.step_number}
              </div>
              <div className={`flex min-w-0 flex-1 flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between ${
                isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {formatStepTiming(step)}
                  </p>
                  <div className={`mt-1 flex items-center gap-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <MessageCircle className="h-3.5 w-3.5 text-sminy-primary" />
                    {step.messages.length} {step.messages.length === 1 ? 'mensagem' : 'mensagens'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onEditStep(step.client_key)}
                  className={`flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium transition-colors sm:w-auto ${
                    isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Edit2 className="h-3.5 w-3.5" />
                  Editar
                </button>
              </div>
            </div>
          );
        })}

        {passos.length < MAX_PASSOS && (
          <button 
            type="button"
            onClick={onAddStep}
            className={`relative z-10 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-3 text-sm font-medium transition-colors ${
              isDark
                ? 'border-gray-600 bg-gray-800 text-gray-300 hover:border-sminy-primary hover:text-sminy-primary'
                : 'border-gray-300 bg-white text-gray-600 hover:border-sminy-primary hover:text-sminy-primary'
            }`}
          >
            <Plus className="h-4 w-4" />
            Adicionar passo
          </button>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// Componente principal de Confirmação
// -------------------------------------------------------------
const Confirmation: React.FC = () => {
  const { isDark } = useTheme();
  const [passos, setPassos] = useState<PassoLocal[]>([]);
  const [editandoPasso, setEditandoPasso] = useState<PassoLocal | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<WhatsAppTemplate[]>([]);
  const [unsupportedTemplateCount, setUnsupportedTemplateCount] = useState(0);
  const [hasOfficialApi, setHasOfficialApi] = useState(false);
  
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [hasSequence, setHasSequence] = useState(false);
  const [activeTab, setActiveTab] = useState('sequence'); // 'sequence' ou 'schedule'
  const [savedStatus, setSavedStatus] = useState<string | null>(null); // null, 'saving', 'saved', 'error'
  const [isDirty, setIsDirty] = useState(false);

  // schedule
  const [scheduleExists, setScheduleExists] = useState(false);
  const [scheduleEditMode, setScheduleEditMode] = useState(false);
  const [scheduleBeforeEdit, setScheduleBeforeEdit] = useState<ScheduleData | null>(null);
  const [showDeleteScheduleModal, setShowDeleteScheduleModal] = useState(false);
  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false);

  const [schedule, setSchedule] = useState<ScheduleData>({
    monday: { enabled: false, start: '08:00', end: '18:00' },
    tuesday: { enabled: false, start: '08:00', end: '18:00' },
    wednesday: { enabled: false, start: '08:00', end: '18:00' },
    thursday: { enabled: false, start: '08:00', end: '18:00' },
    friday: { enabled: false, start: '08:00', end: '18:00' },
    saturday: { enabled: false, start: '08:00', end: '14:00' },
    sunday: { enabled: false, start: '00:00', end: '00:00' },
  });

  const getTemplateByName = (templateName: string): WhatsAppTemplate | undefined => {
    return availableTemplates.find((template) => template.name === templateName);
  };

  const normalizeTemplateContent = (
    content: MensagemLocal['content'],
    fallbackTemplateName?: string,
  ): TemplateMessageConfig | null => {
    const parsedTemplate = parseStoredTemplateMessage(content);
    const templateName = parsedTemplate?.template_name || fallbackTemplateName || '';
    if (!templateName) {
      return null;
    }

    const selectedTemplate = getTemplateByName(templateName);
    if (!selectedTemplate) {
      return parsedTemplate;
    }

    return buildTemplateMessageConfig(selectedTemplate, 'confirmation', parsedTemplate);
  };

  // Ao montar, carrega configuração e schedule
  useEffect(() => {
    const clinicIdStr = localStorage.getItem('clinic_id');
    if (!clinicIdStr) return;
    const clinicIdNum = Number(clinicIdStr);
    if (!clinicIdNum) return;

    carregarConfigExistente(clinicIdNum);
    handleLoadScheduleConfig(clinicIdNum);
    void getClinicWhatsAppProviders(clinicIdNum).then(async (providers) => {
      const hasOfficial = shouldLoadConfirmationTemplates(providers);
      setHasOfficialApi(hasOfficial);

      if (!hasOfficial) {
        setAvailableTemplates([]);
        setUnsupportedTemplateCount(0);
        return;
      }

      try {
        const templates = await listWhatsAppTemplates(clinicIdNum, 'APPROVED', 'confirmation');
        const supportedTemplates = templates.filter((template) => isTemplateSupportedForAutomation(template));
        setAvailableTemplates(supportedTemplates);
        setUnsupportedTemplateCount(templates.length - supportedTemplates.length);
      } catch (error) {
        console.error('Erro ao carregar templates de confirmação:', error);
        setAvailableTemplates([]);
        setUnsupportedTemplateCount(0);
      }
    });
  }, []);

  useEffect(() => {
    const warnAboutUnsavedChanges = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnAboutUnsavedChanges);
    return () => window.removeEventListener('beforeunload', warnAboutUnsavedChanges);
  }, [isDirty]);

  // Limpa mensagens de erro/sucesso após tempo
  useEffect(() => {
    if (erro || sucesso) {
      const timer = setTimeout(() => {
        setErro(null);
        setSucesso(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [erro, sucesso]);

  // -----------------------------------------------------------
  // Carrega config de Confirmação
  // -----------------------------------------------------------
  const carregarConfigExistente = async (clinicId: number) => {
    try {
      setCarregando(true);
      const seq = await getConfirmationSequence(clinicId);

      if (seq === null) {
        setHasSequence(false);
        setPassos([]);
        setIsDirty(false);
      } else {
        setHasSequence(true);

        // Converter steps e guardar ID de step e mensagens
        const passosConvertidos: PassoLocal[] = seq.steps.map((step: any) => ({
          id: step.id,
          client_key: makeClientKey('step', step.id),
          step_number: step.step_number,
          send_before: step.send_after,
          send_before_unit: step.send_after_unit as 'days' | 'hours' | 'minutes',
          send_at_time: step.send_at_time ?? null,
          messages: [...step.messages]
            .sort((a: any, b: any) => (a.position ?? a.id) - (b.position ?? b.id))
            .map((msg: any, messageIndex: number) => ({
            id: msg.id,
            client_key: makeClientKey('message', msg.id),
            position: msg.position ?? messageIndex,
            type: msg.type as MensagemLocal['type'],
            content: msg.type === 'template'
              ? (parseStoredTemplateMessage(msg.content) || msg.content)
              : msg.type === 'location'
                ? normalizeLocationMessage(msg.content)
                : msg.content,
          })),
        }));

        // Ordenamos os passos pelo step_number ascendente
        passosConvertidos.sort((a, b) => a.step_number - b.step_number);

        setPassos(passosConvertidos);
        setIsDirty(false);
      }
    } catch (error: any) {
      setErro('Erro ao carregar configuração existente');
      console.error(error);
    } finally {
      setCarregando(false);
    }
  };

  // -----------------------------------------------------------
  // Carrega config de horários
  // -----------------------------------------------------------
// Função corrigida para carregar configuração de horários
const handleLoadScheduleConfig = async (clinicId: number) => {
  try {
    const resp = await getConfirmationScheduleConfig(clinicId);
    if (resp) {
      // Criar uma nova versão de schedule com os dados da API
      const scheduleData: ScheduleData = {};
      
      // Inicializar com valores padrão
      const defaultSchedule = {
        monday: { enabled: false, start: '08:00', end: '18:00' },
        tuesday: { enabled: false, start: '08:00', end: '18:00' },
        wednesday: { enabled: false, start: '08:00', end: '18:00' },
        thursday: { enabled: false, start: '08:00', end: '18:00' },
        friday: { enabled: false, start: '08:00', end: '18:00' },
        saturday: { enabled: false, start: '08:00', end: '14:00' },
        sunday: { enabled: false, start: '00:00', end: '00:00' },
      };
      
      // Mesclar com dados recebidos
      for (const day in resp.schedule_data) {
        const dayData = resp.schedule_data[day];
        
        if (dayData === null) {
          // Se o dia é null, marcar como desabilitado
          scheduleData[day] = {
            enabled: false,
            start: defaultSchedule[day]?.start || '08:00',
            end: defaultSchedule[day]?.end || '18:00'
          };
        } else {
          // Se o dia tem dados, marcar como habilitado
          scheduleData[day] = {
            enabled: true,
            start: dayData.start || defaultSchedule[day]?.start || '08:00',
            end: dayData.end || defaultSchedule[day]?.end || '18:00'
          };
        }
      }
      
      // Garantir que todos os dias da semana estejam no objeto
      for (const day of daysOfWeek) {
        if (!scheduleData[day.key]) {
          scheduleData[day.key] = defaultSchedule[day.key];
        }
      }
      
      setSchedule(scheduleData);
      setScheduleExists(true);
      setScheduleEditMode(false);
    }
  } catch (error) {
    console.log('Nenhuma config de horários encontrada ou erro:', error);
    setScheduleExists(false);
  }
};

  // -----------------------------------------------------------
  // Salvar config de horários
  // -----------------------------------------------------------
  const handleSaveScheduleConfig = async () => {
    try {
      setSavedStatus('saving');
      const clinicIdStr = localStorage.getItem('clinic_id');
      if (!clinicIdStr) throw new Error('clinic_id não encontrado');
      const clinicIdNum = Number(clinicIdStr);

      // Filtrar apenas dias enabled
      const filteredSchedule: ScheduleData = {};
      for (const dayKey in schedule) {
        const dayData = schedule[dayKey];
        if (dayData.enabled) {
          filteredSchedule[dayKey] = {
            enabled: true,
            start: dayData.start,
            end: dayData.end,
          };
        }
      }

      const schedulePayload = { schedule_data: filteredSchedule };

      if (!scheduleExists) {
        await createConfirmationScheduleConfig(clinicIdNum, schedulePayload);
        setScheduleExists(true);
      } else {
        await updateConfirmationScheduleConfig(clinicIdNum, schedulePayload);
      }

      setScheduleEditMode(false);
      setScheduleBeforeEdit(null);
      setSavedStatus('saved');
      setTimeout(() => setSavedStatus(null), 2000);
    } catch (error) {
      console.error('Erro ao salvar config de horários:', error);
      setSavedStatus('error');
    }
  };

  // -----------------------------------------------------------
  // Deletar config de horários
  // -----------------------------------------------------------
  const handleDeleteScheduleConfig = async () => {
    try {
      setCarregando(true);
      const clinicIdStr = localStorage.getItem('clinic_id');
      if (!clinicIdStr) throw new Error('clinic_id não encontrado');
      const clinicIdNum = Number(clinicIdStr);

      await deleteConfirmationScheduleConfig(clinicIdNum);
      setScheduleExists(false);
      setScheduleEditMode(false);
      setScheduleBeforeEdit(null);

      // Reset local
      setSchedule({
        monday: { enabled: false, start: '08:00', end: '18:00' },
        tuesday: { enabled: false, start: '08:00', end: '18:00' },
        wednesday: { enabled: false, start: '08:00', end: '18:00' },
        thursday: { enabled: false, start: '08:00', end: '18:00' },
        friday: { enabled: false, start: '08:00', end: '18:00' },
        saturday: { enabled: false, start: '08:00', end: '14:00' },
        sunday: { enabled: false, start: '00:00', end: '00:00' },
      });

      setSucesso('Config de horários deletada com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar config de horários:', error);
      setErro('Falha ao deletar config de horários');
    } finally {
      setCarregando(false);
    }
  };
  
  // -----------------------------------------------------------
  // Salvar config (faz upload dos arquivos e manda pro backend)
  // -----------------------------------------------------------
  const salvarConfig = async () => {
    try {
      setSavedStatus('saving');

      const passosNormalizados = renumberSteps(passos);
      const orderError = validateStepOrder(passosNormalizados);
      if (orderError) {
        throw new Error(orderError);
      }

      const clinicIdStr = localStorage.getItem('clinic_id');
      const clientIdStr = localStorage.getItem('client_id');
      if (!clinicIdStr || !clientIdStr) {
        throw new Error('ID da clínica ou cliente não encontrado');
      }

      const clinicIdNum = Number(clinicIdStr);

      // 1) Transformar PassoLocal[] em PassoAPI[] (fazendo upload se houver File)
      const passosAPI: PassoAPI[] = await Promise.all(
        passosNormalizados.map(async (passoLocal) => {
          // Ordenamos as mensagens pelo ID
          const messagesAPI = await Promise.all(
            passoLocal.messages.map(async (msgLocal) => {
              // Verificamos se é um arquivo
              if (['image', 'audio', 'video'].includes(msgLocal.type) && isFile(msgLocal.content)) {
                // upload
                const file = msgLocal.content as File;
                const limiteArquivo = LIMITE_ARQUIVOS[msgLocal.type];
                if (file.size > limiteArquivo) {
                  throw new Error(
                    `Arquivo muito grande. Máx p/ ${msgLocal.type}: ${limiteArquivo / (1024 * 1024)}MB`
                  );
                }
                const resultadoUpload = await uploadFile(file);
                return {
                  id: msgLocal.id,
                  type: msgLocal.type,
                  content: resultadoUpload.path,
                  position: msgLocal.position,
                } as MensagemAPI;
              } else if (msgLocal.type === 'template') {
                const templateConfig = normalizeTemplateContent(msgLocal.content);
                if (!templateConfig?.template_name) {
                  throw new Error('Selecione um template Meta aprovado para todas as mensagens do tipo template.');
                }

                const hasInvalidParameter = templateConfig.components.some((component) =>
                  (component.parameters || []).some((parameter) => {
                    if (!parameter?.source) {
                      return true;
                    }
                    if (parameter.source === 'literal') {
                      return !parameter.value?.trim();
                    }
                    return !getTemplateVariableOption(parameter.source, 'confirmation');
                  }),
                );

                if (hasInvalidParameter) {
                  throw new Error('Preencha todas as variáveis do template antes de salvar o passo.');
                }

                return {
                  id: msgLocal.id,
                  type: msgLocal.type,
                  content: serializeTemplateMessage(templateConfig),
                  position: msgLocal.position,
                } as MensagemAPI;
              } else if (msgLocal.type === 'location') {
                return {
                  id: msgLocal.id,
                  type: msgLocal.type,
                  content: locationMessageForApi(msgLocal.content, passoLocal.step_number),
                  position: msgLocal.position,
                } as MensagemAPI;
              } else if (typeof msgLocal.content === 'string') {
                return {
                  id: msgLocal.id,
                  type: msgLocal.type,
                  content: msgLocal.content,
                  position: msgLocal.position,
                } as MensagemAPI;
              } else {
                throw new Error('Tipo de conteúdo inesperado.');
              }
            })
          );

          return {
            id: passoLocal.id,
            step_number: passoLocal.step_number,
            send_after: passoLocal.send_before,
            send_after_unit: passoLocal.send_before_unit,
            send_at_time: passoLocal.send_at_time || null,
            messages: messagesAPI,
          } as PassoAPI;
        })
      );

      // 2) Montamos o payload final
      const payload = {
        clinic_id: clinicIdNum,
        client_id: clientIdStr,
        name: 'Sequência de Confirmação',
        description: 'Mensagens automáticas de confirmação',
        steps: passosAPI,
      };

      // 3) create ou update
      if (hasSequence) {
        await updateConfirmationSequence(clinicIdNum, payload);
        setSavedStatus('saved');
      } else {
        await createConfirmationSequence(clinicIdNum, payload);
        setHasSequence(true);
        setSavedStatus('saved');
      }

      setTimeout(() => setSavedStatus(null), 2000);

      // 4) Recarrega do GET para atualizar e ordenar local
      await carregarConfigExistente(clinicIdNum);
      setEditandoPasso(null);
      setIsDirty(false);
    } catch (err: any) {
      setErro(err?.message || 'Falha ao salvar configuração');
      setSavedStatus('error');
      console.error(err);
    }
  };

  // -----------------------------------------------------------
  // Adicionar passo / editar passo / adicionar mensagens
  // -----------------------------------------------------------
  const clonePasso = (passo: PassoLocal): PassoLocal => ({
    ...passo,
    messages: passo.messages.map((message) => ({ ...message })),
  });

  const adicionarPasso = (position: number = passos.length) => {
    if (passos.length >= MAX_PASSOS) {
      setErro(`Número máximo de passos (${MAX_PASSOS}) atingido`);
      return;
    }
    const timingReference = passos[position] || passos[position - 1];
    const novoPasso: PassoLocal = {
      client_key: makeClientKey('step'),
      step_number: position + 1,
      send_before: timingReference?.send_before || 1,
      send_before_unit: timingReference?.send_before_unit || 'days',
      send_at_time: null,
      messages: [],
    };
    setEditandoPasso(clonePasso(novoPasso));
  };

  const editarPasso = (stepKey: string) => {
    const passo = passos.find((item) => item.client_key === stepKey);
    if (passo) {
      setEditandoPasso(clonePasso(passo));
    }
  };

  const atualizarPasso = (passoAtualizado: PassoLocal) => {
    setEditandoPasso(passoAtualizado);
  };

  const aplicarEdicaoPasso = () => {
    if (!editandoPasso) return;
    const existingIndex = passos.findIndex((step) => step.client_key === editandoPasso.client_key);
    const editedSteps = [...passos];
    if (existingIndex >= 0) {
      editedSteps[existingIndex] = clonePasso(editandoPasso);
    } else {
      editedSteps.splice(Math.max(0, editandoPasso.step_number - 1), 0, clonePasso(editandoPasso));
    }
    const nextSteps = renumberSteps(editedSteps);
    const orderError = validateStepOrder(nextSteps);
    if (orderError) {
      setErro(orderError);
      return;
    }
    setPassos(nextSteps);
    setEditandoPasso(null);
    setIsDirty(true);
  };

  const fecharEdicaoPasso = () => {
    setEditandoPasso(null);
  };

  const excluirPassoEmEdicao = () => {
    if (!editandoPasso) return;
    const existsInFlow = passos.some((step) => step.client_key === editandoPasso.client_key);

    if (!existsInFlow) {
      setEditandoPasso(null);
      setShowDeleteStepModal(false);
      return;
    }
    if (passos.length <= 1) {
      setErro('O fluxo precisa manter pelo menos um passo.');
      setShowDeleteStepModal(false);
      return;
    }

    setPassos(removeStepByKey(passos, editandoPasso.client_key));
    setEditandoPasso(null);
    setShowDeleteStepModal(false);
    setIsDirty(true);
  };

  const adicionarMensagem = (_numeroPasso?: number, position?: number) => {
    if (!editandoPasso) return;
    const messages = [...editandoPasso.messages];
    const insertionPoint = position ?? messages.length;
    messages.splice(insertionPoint, 0, {
      client_key: makeClientKey('message'),
      position: insertionPoint,
      type: 'text',
      content: '',
    });
    setEditandoPasso({
      ...editandoPasso,
      messages: messages.map((message, index) => ({ ...message, position: index })),
    });
  };

  const deletarMensagem = (_numeroPasso: number, indiceMensagem: number) => {
    if (!editandoPasso) return;
    const messages = editandoPasso.messages
      .filter((_, index) => index !== indiceMensagem)
      .map((message, index) => ({ ...message, position: index }));
    setEditandoPasso({ ...editandoPasso, messages });
  };

  const alterarMensagem = (
    numeroPasso: number,
    indiceMensagem: number,
    messageType: MensagemLocal['type'],
    conteudo: string | File | any
  ) => {
    if (!editandoPasso) return;
    const oldMsg = editandoPasso.messages[indiceMensagem];

    const updatedMsg: MensagemLocal = {
      id: oldMsg?.id,
      client_key: oldMsg?.client_key || makeClientKey('message'),
      position: oldMsg?.position ?? indiceMensagem,
      type: messageType,
      content: conteudo,
    };
    const mensagensAtualizadas = [...editandoPasso.messages];
    mensagensAtualizadas[indiceMensagem] = updatedMsg;
    setEditandoPasso({ ...editandoPasso, messages: mensagensAtualizadas });
  };
  
  const handleInsertPlaceholder = (
    stepNumber: number,
    messageIndex: number,
    placeholder: string
  ) => {
    if (!editandoPasso) return;
    const mensagem = editandoPasso.messages[messageIndex];
    
    if (mensagem.type === 'text' && typeof mensagem.content === 'string') {
      const updatedContent = mensagem.content + ' ' + placeholder;
      
      alterarMensagem(stepNumber, messageIndex, 'text', updatedContent);
    }
  };

  const moveStep = (from: number, to: number) => {
    if (to < 0 || to >= passos.length) return;
    const nextSteps = renumberSteps(reorderItems(passos, from, to));
    setPassos(nextSteps);
    setIsDirty(true);
  };

  const handleStepDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    moveStep(result.source.index, result.destination.index);
  };

  const moveMessage = (from: number, to: number) => {
    if (!editandoPasso || to < 0 || to >= editandoPasso.messages.length) return;
    const messages = reorderItems(editandoPasso.messages, from, to)
      .map((message, index) => ({ ...message, position: index }));
    setEditandoPasso({ ...editandoPasso, messages });
  };

  const handleMessageDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    moveMessage(result.source.index, result.destination.index);
  };

  const enabledScheduleDays = daysOfWeek.filter((day) => schedule[day.key]?.enabled);
  const isEditingSchedule = scheduleEditMode || !scheduleExists;
  const isCreatingStep = editandoPasso
    ? !passos.some((step) => step.client_key === editandoPasso.client_key)
    : false;

  // Render loading state
  if (carregando && passos.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-700 border-t-transparent"></div>
      </div>
    );
  }

  // -----------------------------------------------------------
  // Render
  // -----------------------------------------------------------
  return (
    <div className={`min-h-screen w-full ${
      isDark ? 'bg-[#111827] text-gray-200' : 'bg-gradient-to-b from-gray-50 to-white text-gray-800'
    }`}>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header com logo e título */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-8">
          <div>
            <h1 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Sequência de confirmação
            </h1>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Defina quando e em qual ordem os pacientes receberão cada mensagem.
            </p>
          </div>
          
          <div className={`flex space-x-1 p-1 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <button 
              onClick={() => setActiveTab('sequence')}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${
                activeTab === 'sequence' 
                  ? 'bg-sminy-primary text-white'
                  : isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              Mensagens
            </button>
            <button 
              onClick={() => setActiveTab('schedule')}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${
                activeTab === 'schedule' 
                  ? 'bg-sminy-primary text-white'
                  : isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Horários
            </button>
          </div>
        </div>
        
        {/* Alertas */}
        {erro && (
          <div className="fixed bottom-6 right-6 py-2 px-4 bg-red-100 text-red-700 rounded-md shadow-lg flex items-center max-w-md">
            <AlertCircle className="w-5 h-5 mr-2" />
            {erro}
          </div>
        )}
        {sucesso && (
          <div className="fixed bottom-6 right-6 py-2 px-4 bg-green-100 text-green-700 rounded-md shadow-lg flex items-center max-w-md">
            <Check className="w-5 h-5 mr-2" />
            {sucesso}
          </div>
        )}
        
        {/* Save status indicator */}
        {savedStatus && (
          <div className={`fixed bottom-6 right-6 py-2 px-4 rounded-md shadow-lg flex items-center gap-2 ${
            savedStatus === 'saved' ? 'bg-green-100 text-green-700' :
            'bg-red-100 text-red-700'
          }`}>
            {savedStatus === 'saving' ? 
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                <span>Salvando...</span>
              </> :
             savedStatus === 'saved' ? 
              <>
                <Check className="w-4 h-4" />
                <span>Salvo com sucesso!</span>
              </> :
              <>
                <AlertCircle className="w-4 h-4" />
                <span>Erro ao salvar</span>
              </>
            }
          </div>
        )}
        
        {/* Sequence tab */}
        {activeTab === 'sequence' && (
          <div className="mb-8">
            {/* Visão de Overview (quando não está editando um passo) */}
            {!editandoPasso ? (
              <>
                <div className={`rounded-2xl border p-4 shadow-xl mb-6 ${
                  isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                    <div>
                      <h2 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                        Fluxo de confirmação
                      </h2>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Organize do envio mais distante para o mais próximo da consulta.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isDirty && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                          Alterações não salvas
                        </span>
                      )}
                    <button 
                      className="rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white hover:bg-sminy-primary/90 disabled:cursor-not-allowed disabled:opacity-50 flex items-center gap-2"
                      onClick={salvarConfig}
                      disabled={passos.length === 0 || carregando || savedStatus === 'saving' || !isDirty}
                    >
                      <Save className="w-4 h-4" />
                      Salvar fluxo
                    </button>
                    </div>
                  </div>

                  <div className={`mb-5 flex gap-3 rounded-xl border p-3 ${
                    isDark ? 'border-sminy-primary/30 bg-sminy-primary/10' : 'border-sminy-primary/20 bg-sminy-primary/5'
                  }`}>
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 text-sminy-primary" />
                    <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Ao salvar, a nova versão valerá para novos agendamentos. Confirmações já programadas permanecem na versão anterior.
                    </p>
                  </div>
                  
                  {/* Timeline visualization */}
                  {passos.length > 0 ? (
                    <TimelineIndicator 
                      passos={passos} 
                      onEditStep={editarPasso} 
                      onAddStep={() => adicionarPasso()}
                    />
                  ) : (
                    <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                      <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 mb-4">
                        Nenhum passo configurado. Crie sua sequência de confirmação.
                      </p>
                      <button 
                        onClick={() => adicionarPasso()}
                        className="rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white hover:bg-sminy-primary/90"
                      >
                        <Plus className="w-4 h-4 inline mr-2" />
                        Adicionar primeiro passo
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Passos reordenáveis */}
                {passos.length > 0 && (
                  <DragDropContext onDragEnd={handleStepDragEnd}>
                    <Droppable droppableId="confirmation-steps">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                          {passos.map((passo, stepIndex) => (
                            <Draggable key={passo.client_key} draggableId={passo.client_key} index={stepIndex}>
                              {(dragProvided, snapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  className={`rounded-2xl border shadow-xl transition ${
                                    snapshot.isDragging ? 'ring-2 ring-sminy-primary' : ''
                                  } ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}
                                >
                                  <div className={`flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between ${
                                    isDark ? 'border-gray-700' : 'border-gray-100'
                                  }`}>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        {...dragProvided.dragHandleProps}
                                        aria-label={`Arrastar passo ${passo.step_number}`}
                                        className={`rounded-xl p-2 ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}
                                      >
                                        <GripVertical className="h-5 w-5" />
                                      </button>
                                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-sminy-primary text-sm font-semibold text-white">
                                        {passo.step_number}
                                      </div>
                                      <div>
                                        <h3 className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                                          Passo {passo.step_number}
                                        </h3>
                                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {formatStepTiming(passo)} · {passo.messages.length} mensagem(ns)
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 self-end sm:self-auto">
                                      <button type="button" onClick={() => moveStep(stepIndex, stepIndex - 1)} disabled={stepIndex === 0} aria-label="Mover passo para cima" className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-30">
                                        <ArrowUp className="h-4 w-4" />
                                      </button>
                                      <button type="button" onClick={() => moveStep(stepIndex, stepIndex + 1)} disabled={stepIndex === passos.length - 1} aria-label="Mover passo para baixo" className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-30">
                                        <ArrowDown className="h-4 w-4" />
                                      </button>
                                      <button type="button" onClick={() => editarPasso(passo.client_key)} className="rounded-xl px-3 py-2 text-sm font-medium text-sminy-primary hover:bg-sminy-primary/10">
                                        <Edit2 className="mr-1 inline h-4 w-4" /> Editar
                                      </button>
                                    </div>
                                  </div>
                                  <div className="space-y-2 p-4">
                                    {passo.messages.length > 0 ? passo.messages.map((message, messageIndex) => (
                                      <div key={message.client_key} className={`rounded-xl px-3 py-2 text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
                                        <span className="font-semibold">Mensagem {messageIndex + 1}</span>
                                        <span className="mx-1">·</span>
                                        <span className="capitalize">{message.type}</span>
                                        {message.type === 'text' && typeof message.content === 'string' && (
                                          <p className="mt-1 line-clamp-2">{message.content || 'Mensagem ainda vazia'}</p>
                                        )}
                                        {message.type === 'location' && (
                                          <p className="mt-1 flex items-center gap-1.5">
                                            <MapPin className="h-3.5 w-3.5 text-sminy-primary" />
                                            {normalizeLocationMessage(message.content).name || 'Pin de localização'}
                                          </p>
                                        )}
                                        {['image', 'audio', 'video'].includes(message.type) && message.content && (
                                          <PreviewArquivo tipo={message.type} conteudo={message.content} />
                                        )}
                                      </div>
                                    )) : (
                                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Nenhuma mensagem adicionada. Abra o passo para configurar.
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2 p-3">
                                    <button type="button" onClick={() => adicionarPasso(stepIndex)} disabled={passos.length >= MAX_PASSOS} className={`rounded-xl border px-3 py-2 text-xs font-medium disabled:opacity-40 ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                      <Plus className="mr-1 inline h-3 w-3" /> Adicionar antes
                                    </button>
                                    <button type="button" onClick={() => adicionarPasso(stepIndex + 1)} disabled={passos.length >= MAX_PASSOS} className={`rounded-xl border px-3 py-2 text-xs font-medium disabled:opacity-40 ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                      <Plus className="mr-1 inline h-3 w-3" /> Adicionar depois
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </>
            ) : (
              /* Formulário de edição de passo */
              <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className={`flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 ${
                  isDark ? 'border-gray-700 bg-[#111827]/60' : 'border-gray-100 bg-sminy-primary/5'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-sminy-primary text-sm font-semibold text-white shadow-sm">
                      {editandoPasso.step_number}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                        {isCreatingStep ? 'Criar novo passo' : `Editar passo ${editandoPasso.step_number}`}
                      </h3>
                      <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Configure o momento e a ordem das mensagens deste contato.
                      </p>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    aria-label="Fechar editor do passo"
                    className={`rounded-xl p-2 transition-colors ${
                      isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    }`}
                    onClick={fecharEdicaoPasso}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="p-4 sm:p-6">
                  {/* Timing configuration */}
                  <div className={`mb-6 rounded-2xl border p-4 ${
                    isDark ? 'border-gray-700 bg-[#111827]/50' : 'border-gray-200 bg-gray-50/70'
                  }`}>
                    <div className="mb-4 flex items-start gap-3">
                      <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-sminy-primary/10 text-sminy-primary">
                        <Clock className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          Tempo antes da consulta
                        </h4>
                        <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Defina quando este passo será executado.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:max-w-xl sm:grid-cols-[140px_minmax(0,1fr)]">
                      <label className="space-y-1.5">
                        <span className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Quantidade
                        </span>
                        <input
                          type="number"
                          min={editandoPasso.send_at_time ? '0' : '1'}
                          step="1"
                          value={editandoPasso.send_before}
                          onChange={(e) => atualizarPasso({
                            ...editandoPasso,
                            send_before: parseInt(e.target.value || '0', 10),
                          })}
                          className={`w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                            isDark
                              ? 'border-gray-600 bg-gray-700 text-white'
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                      </label>
                      <label className="space-y-1.5">
                        <span className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Unidade
                        </span>
                        <select
                          value={editandoPasso.send_before_unit}
                          onChange={(e) => atualizarPasso({
                            ...editandoPasso,
                            send_before_unit: e.target.value as 'days' | 'hours' | 'minutes',
                            send_at_time: e.target.value === 'days' ? editandoPasso.send_at_time : null,
                          })}
                          className={`w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                            isDark
                              ? 'border-gray-600 bg-gray-700 text-white'
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        >
                          {timeUnits.map(unit => (
                            <option key={unit.value} value={unit.value}>{unit.label}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {editandoPasso.send_before_unit === 'days' ? (
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:max-w-xl sm:grid-cols-[minmax(0,220px)_minmax(0,1fr)] sm:items-end">
                        <label className="space-y-1.5">
                          <span className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Horário do envio
                          </span>
                          <input
                            type="time"
                            value={editandoPasso.send_at_time || ''}
                            onChange={(e) => atualizarPasso({
                              ...editandoPasso,
                              send_at_time: e.target.value || null,
                            })}
                            className={`w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                              isDark
                                ? 'border-gray-600 bg-gray-700 text-white'
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}
                          />
                        </label>
                        <p className={`pb-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {editandoPasso.send_at_time
                            ? 'Este passo será enviado neste horário local da clínica.'
                            : 'Sem horário definido, o envio mantém o horário da consulta.'}
                        </p>
                      </div>
                    ) : (
                      <p className={`mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        O envio será calculado a partir do horário marcado para a consulta.
                      </p>
                    )}
                  </div>
                  
                  {/* Messages */}
                  <div className="mb-6">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          Mensagens
                        </h4>
                        <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Mensagens enviadas na ordem abaixo.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => adicionarMensagem(editandoPasso.step_number)}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-sminy-primary/10 px-3 py-2 text-sm font-medium text-sminy-primary transition-colors hover:bg-sminy-primary/20 sm:w-auto"
                      >
                        <Plus className="h-4 w-4" />
                        Adicionar mensagem
                      </button>
                    </div>

                    {hasOfficialApi && availableTemplates.length === 0 && (
                      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        Nenhum template Meta compatível foi encontrado para `confirmation`. Use um template aprovado com BODY/HEADER textual em `/prompt/templates` para usar esse tipo de mensagem.
                      </div>
                    )}
                    {hasOfficialApi && unsupportedTemplateCount > 0 && (
                      <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
                        {unsupportedTemplateCount} template(s) foram ocultados por usarem componentes ainda não suportados nesta automação, como header de mídia ou botão dinâmico.
                      </div>
                    )}
                    
                    {/* Mensagens existentes */}
                    {editandoPasso.messages.length > 0 ? (
                      <DragDropContext onDragEnd={handleMessageDragEnd}>
                        <Droppable droppableId={`messages-${editandoPasso.client_key}`}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                      {editandoPasso.messages.map((message, index) => (
                        <Draggable key={message.client_key} draggableId={message.client_key} index={index}>
                          {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={`mb-4 rounded-2xl border p-4 ${
                            snapshot.isDragging ? 'ring-2 ring-sminy-primary' : ''
                          } ${isDark ? 'border-gray-600 bg-gray-700/60' : 'border-gray-200 bg-gray-50/70'}`}
                        >
                          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-center gap-2">
                              <button
                                type="button"
                                {...dragProvided.dragHandleProps}
                                aria-label={`Arrastar mensagem ${index + 1}`}
                                className={`rounded-lg p-1.5 text-gray-400 transition-colors ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                              >
                                <GripVertical className="h-4 w-4" />
                              </button>
                              <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg bg-sminy-primary/10 text-xs font-semibold text-sminy-primary">
                                {index + 1}
                              </span>
                              <select
                                value={message.type}
                                onChange={(e) => alterarMensagem(
                                  editandoPasso.step_number,
                                  index,
                                  e.target.value as MensagemLocal['type'],
                                  (() => {
                                    const nextType = e.target.value as MensagemLocal['type'];
                                    if (nextType === 'text') {
                                      return message.type === 'text' && typeof message.content === 'string' ? message.content : '';
                                    }
                                    if (nextType === 'location') {
                                      return message.type === 'location'
                                        ? normalizeLocationMessage(message.content)
                                        : { latitude: '', longitude: '', name: '', address: '' };
                                    }
                                    if (nextType === 'template') {
                                      if (message.type === 'template') {
                                        return normalizeTemplateContent(message.content);
                                      }
                                      return availableTemplates[0]
                                        ? buildTemplateMessageConfig(availableTemplates[0], 'confirmation')
                                        : {
                                          template_name: '',
                                          language: 'pt_BR',
                                          components: [],
                                        };
                                    }
                                    return '';
                                  })()
                                )}
                                className={`min-w-0 rounded-xl border px-3 py-2 text-sm font-medium transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                  isDark
                                    ? 'border-gray-600 bg-gray-700 text-gray-100'
                                    : 'border-gray-300 bg-white text-gray-700'
                                }`}
                              >
                                {messageTypes.map(type => (
                                  <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex flex-wrap items-center gap-1 self-end sm:self-auto">
                              <>
                                <button type="button" onClick={() => adicionarMensagem(editandoPasso.step_number, index)} className="rounded-lg px-2 py-1 text-xs text-sminy-primary hover:bg-sminy-primary/10" aria-label="Inserir mensagem antes">
                                  + antes
                                </button>
                                <button type="button" onClick={() => adicionarMensagem(editandoPasso.step_number, index + 1)} className="rounded-lg px-2 py-1 text-xs text-sminy-primary hover:bg-sminy-primary/10" aria-label="Inserir mensagem depois">
                                  + depois
                                </button>
                              </>
                              <button type="button" onClick={() => moveMessage(index, index - 1)} disabled={index === 0} aria-label="Mover mensagem para cima" className={`rounded-lg p-1.5 text-gray-500 disabled:opacity-30 ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                                <ArrowUp className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => moveMessage(index, index + 1)} disabled={index === editandoPasso.messages.length - 1} aria-label="Mover mensagem para baixo" className={`rounded-lg p-1.5 text-gray-500 disabled:opacity-30 ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                                <ArrowDown className="h-4 w-4" />
                              </button>
                              <button type="button" className={`rounded-lg p-1.5 text-red-500 transition-colors ${isDark ? 'hover:bg-red-500/10 hover:text-red-300' : 'hover:bg-red-50 hover:text-red-700'}`} onClick={() => deletarMensagem(editandoPasso.step_number, index)} aria-label="Remover mensagem">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {message.type === 'text' ? (
                            <div>
                              {/* Botões de placeholder */}
                              <div className="mb-2 flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleInsertPlaceholder(editandoPasso.step_number, index, '[data-consulta]')}
                                  className={`rounded-lg px-2 py-1 text-xs transition-colors ${
                                    isDark ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  [data-consulta]
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleInsertPlaceholder(editandoPasso.step_number, index, '*[data-consulta]*')}
                                  className={`rounded-lg px-2 py-1 text-xs transition-colors ${
                                    isDark ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  *[data-consulta]*
                                </button>
                              </div>
                              
                              <textarea 
                                value={typeof message.content === 'string' ? message.content : ''}
                                onChange={(e) => alterarMensagem(
                                  editandoPasso.step_number, 
                                  index, 
                                  'text', 
                                  e.target.value
                                )}
                                rows={3}
                                className={`w-full resize-none rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                  isDark
                                    ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                                    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                                }`}
                                placeholder="Digite sua mensagem aqui..."
                              />
                            </div>
                          ) : message.type === 'template' ? (
                            <div className="space-y-3">
                              <select
                                value={normalizeTemplateContent(message.content)?.template_name || ''}
                                onChange={(e) => alterarMensagem(
                                  editandoPasso.step_number,
                                  index,
                                  'template',
                                  (() => {
                                    const selectedTemplate = availableTemplates.find(
                                      (template) => template.name === e.target.value,
                                    );
                                    if (!selectedTemplate) {
                                      return {
                                        template_name: '',
                                        language: 'pt_BR',
                                        components: [],
                                      };
                                    }
                                    return buildTemplateMessageConfig(selectedTemplate, 'confirmation');
                                  })()
                                )}
                                className={`w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                  isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                                }`}
                              >
                                <option value="">Selecione um template aprovado</option>
                                {availableTemplates.map((template) => (
                                  <option key={template.id} value={template.name}>
                                    {template.name}
                                  </option>
                                ))}
                              </select>

                              {(() => {
                                const templateConfig = normalizeTemplateContent(message.content);
                                const selectedTemplate = templateConfig?.template_name
                                  ? getTemplateByName(templateConfig.template_name)
                                  : undefined;
                                const analysis = selectedTemplate
                                  ? analyzeTemplateSupport(selectedTemplate)
                                  : null;

                                if (!templateConfig || !selectedTemplate) {
                                  return null;
                                }

                                return (
                                  <>
                                    <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'}`}>
                                      <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Template selecionado
                                      </p>
                                      <p className={`mt-1 text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                        {templateConfig.template_name}
                                      </p>
                                      {!!analysis?.bodyText && (
                                        <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {analysis.bodyText}
                                        </p>
                                      )}
                                    </div>

                                    {analysis && analysis.slots.length > 0 && (
                                      <div className="space-y-3">
                                        {analysis.slots.map((slot) => {
                                          const component = templateConfig.components.find(
                                            (item) => item.type.toUpperCase() === slot.componentType,
                                          );
                                          const parameter = component?.parameters?.[slot.parameterIndex] || {
                                            type: 'text' as const,
                                            source: 'literal',
                                            value: '',
                                          };

                                          return (
                                            <div
                                              key={slot.id}
                                              className={`rounded-xl border p-3 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'}`}
                                            >
                                              <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {slot.componentType} {slot.placeholder}
                                              </p>

                                              <select
                                                value={parameter.source}
                                                onChange={(e) => {
                                                  const nextConfig = normalizeTemplateContent(templateConfig, templateConfig.template_name);
                                                  if (!nextConfig) return;
                                                  const nextComponent = nextConfig.components.find(
                                                    (item) => item.type.toUpperCase() === slot.componentType,
                                                  );
                                                  if (!nextComponent) return;
                                                  nextComponent.parameters[slot.parameterIndex] = {
                                                    type: 'text',
                                                    source: e.target.value,
                                                    value: e.target.value === 'literal'
                                                      ? (nextComponent.parameters[slot.parameterIndex]?.value || '')
                                                      : undefined,
                                                  };
                                                  alterarMensagem(editandoPasso.step_number, index, 'template', nextConfig);
                                                }}
                                                className={`mt-2 w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                                  isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                                                }`}
                                              >
                                                {getTemplateVariableOptions('confirmation').map((option) => (
                                                  <option key={option.key} value={option.key}>
                                                    {option.label}
                                                  </option>
                                                ))}
                                              </select>

                                              <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {getTemplateVariableOption(parameter.source, 'confirmation')?.description || 'Selecione a origem do valor.'}
                                              </p>

                                              {parameter.source === 'literal' && (
                                                <input
                                                  type="text"
                                                  value={parameter.value || ''}
                                                  onChange={(e) => {
                                                    const nextConfig = normalizeTemplateContent(templateConfig, templateConfig.template_name);
                                                    if (!nextConfig) return;
                                                    const nextComponent = nextConfig.components.find(
                                                      (item) => item.type.toUpperCase() === slot.componentType,
                                                    );
                                                    if (!nextComponent) return;
                                                    nextComponent.parameters[slot.parameterIndex] = {
                                                      type: 'text',
                                                      source: 'literal',
                                                      value: e.target.value,
                                                    };
                                                    alterarMensagem(editandoPasso.step_number, index, 'template', nextConfig);
                                                  }}
                                                  placeholder="Digite o texto fixo para este parametro"
                                                  className={`mt-2 w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                                    isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                                                  }`}
                                                />
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : message.type === 'location' ? (
                            <div className={`mt-2 space-y-3 rounded-xl border p-4 ${
                              isDark ? 'border-gray-600 bg-[#111827]/40' : 'border-gray-200 bg-white'
                            }`}>
                              <div className="flex items-start gap-3">
                                <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-sminy-primary/10 text-sminy-primary">
                                  <MapPin className="h-4 w-4" />
                                </div>
                                <div>
                                  <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                    Pin de localização do WhatsApp
                                  </p>
                                  <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Informe as coordenadas da clínica para enviar um ponto clicável no mapa.
                                  </p>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {([
                                  ['latitude', 'Latitude'],
                                  ['longitude', 'Longitude'],
                                ] as const).map(([field, label]) => {
                                  const location = normalizeLocationMessage(message.content);
                                  return (
                                    <label key={field} className="space-y-1.5">
                                      <span className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {label}
                                      </span>
                                      <input
                                        type="number"
                                        step="any"
                                        value={location[field]}
                                        onChange={(e) => alterarMensagem(
                                          editandoPasso.step_number,
                                          index,
                                          'location',
                                          { ...location, [field]: e.target.value },
                                        )}
                                        placeholder="Coordenada decimal"
                                        className={`w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                          isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                                        }`}
                                      />
                                    </label>
                                  );
                                })}
                                {([
                                  ['name', 'Nome exibido'],
                                  ['address', 'Endereço'],
                                ] as const).map(([field, label]) => {
                                  const location = normalizeLocationMessage(message.content);
                                  return (
                                    <label key={field} className="space-y-1.5">
                                      <span className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {label}
                                      </span>
                                      <input
                                        type="text"
                                        maxLength={255}
                                        value={location[field]}
                                        onChange={(e) => alterarMensagem(
                                          editandoPasso.step_number,
                                          index,
                                          'location',
                                          { ...location, [field]: e.target.value },
                                        )}
                                        placeholder={field === 'name' ? 'OdontoCentro Botucatu' : 'Rua, número, cidade'}
                                        className={`w-full rounded-xl border p-3 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                          isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                                        }`}
                                      />
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2">
                              {/* Arquivo já enviado */}
                              {typeof message.content === 'string' && message.content && (
                                <div className="mb-2">
                                  <p className={`mb-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Arquivo atual: {message.content.split('/').pop()}
                                  </p>
                                  <PreviewArquivo tipo={message.type} conteudo={message.content} />
                                </div>
                              )}
                              
                              {/* Upload de arquivo */}
                              <div className={`flex flex-col items-center justify-center rounded-xl border border-dashed p-4 text-center ${
                                isDark ? 'border-gray-600 bg-[#111827]/40' : 'border-gray-300 bg-white'
                              }`}>
                                {isFile(message.content) ? (
                                  <>
                                    <File className="mb-2 h-8 w-8 text-sminy-primary" />
                                    <p className={`mb-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{(message.content as File).name}</p>
                                  </>
                                ) : (
                                  <File className={`mb-2 h-8 w-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                )}
                                
                                <p className={`mb-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {message.type === 'image' && `Máx ${LIMITE_ARQUIVOS.image / (1024 * 1024)}MB (imagens)`}
                                  {message.type === 'audio' && `Máx ${LIMITE_ARQUIVOS.audio / (1024 * 1024)}MB (áudios)`}
                                  {message.type === 'video' && `Máx ${LIMITE_ARQUIVOS.video / (1024 * 1024)}MB (vídeos)`}
                                </p>
                                
                                <input
                                  type="file"
                                  id={`file-${editandoPasso.step_number}-${index}`}
                                  className="hidden"
                                  accept={
                                    message.type === 'image' ? 'image/*' :
                                    message.type === 'audio' ? 'audio/*' : 'video/*'
                                  }
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      alterarMensagem(
                                        editandoPasso.step_number,
                                        index,
                                        message.type,
                                        e.target.files[0]
                                      );
                                    }
                                  }}
                                />
                                <label 
                                  htmlFor={`file-${editandoPasso.step_number}-${index}`}
                                  className="cursor-pointer rounded-xl bg-sminy-primary/10 px-3 py-2 text-xs font-medium text-sminy-primary transition-colors hover:bg-sminy-primary/20"
                                >
                                  {isFile(message.content) ? 'Alterar arquivo' : 'Selecionar arquivo'}
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                          )}
                        </Draggable>
                      ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ) : (
                      <div className={`rounded-2xl border border-dashed p-8 text-center ${
                        isDark ? 'border-gray-600 bg-[#111827]/40' : 'border-gray-300 bg-gray-50/70'
                      }`}>
                        <div className={`mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl ${
                          isDark ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <MessageCircle className={`h-6 w-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                        <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          Nenhuma mensagem adicionada
                        </h4>
                        <p className={`mb-4 mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Adicione a primeira mensagem que será enviada neste passo.
                        </p>
                        <button
                          type="button"
                          onClick={() => adicionarMensagem(editandoPasso.step_number)}
                          className="inline-flex items-center gap-2 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90"
                        >
                          <Plus className="h-4 w-4" />
                          Adicionar primeira mensagem
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Action buttons */}
                  <div className={`flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <div>
                      {!isCreatingStep && (
                        <button
                          type="button"
                          disabled={passos.length <= 1}
                          title={passos.length <= 1 ? 'O fluxo precisa manter pelo menos um passo.' : 'Excluir este passo'}
                          className={`flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto ${
                            isDark
                              ? 'border-red-500/40 text-red-300 hover:bg-red-500/10'
                              : 'border-red-200 text-red-600 hover:bg-red-50'
                          }`}
                          onClick={() => setShowDeleteStepModal(true)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Excluir passo
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col-reverse gap-3 sm:flex-row">
                      <button
                        type="button"
                        className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                          isDark
                            ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={fecharEdicaoPasso}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="flex items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90"
                        onClick={aplicarEdicaoPasso}
                      >
                        <Check className="h-4 w-4" />
                        {isCreatingStep ? 'Adicionar ao fluxo' : 'Aplicar alterações'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Schedule tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-5">
            <div className={`overflow-hidden rounded-2xl border shadow-xl ${
              isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
            }`}>
              <div className={`flex flex-col gap-4 border-b p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6 ${
                isDark ? 'border-gray-700' : 'border-gray-100'
              }`}>
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-sminy-primary/10 text-sminy-primary">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                      Horários de envio
                    </h2>
                    <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Defina em quais dias e períodos as confirmações podem ser enviadas.
                    </p>
                  </div>
                </div>

                {scheduleExists && !scheduleEditMode ? (
                  <button 
                    type="button"
                    className={`flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors sm:w-auto ${
                      isDark
                        ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      setScheduleBeforeEdit(
                        Object.keys(schedule).reduce<ScheduleData>((snapshot, day) => {
                          snapshot[day] = { ...schedule[day] };
                          return snapshot;
                        }, {}),
                      );
                      setScheduleEditMode(true);
                    }}
                  >
                    <Edit2 className="h-4 w-4" />
                    Editar horários
                  </button>
                ) : (
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90 sm:w-auto"
                    onClick={handleSaveScheduleConfig}
                  >
                    <Save className="h-4 w-4" />
                    Salvar horários
                  </button>
                )}
              </div>

              <div className="space-y-5 p-4 sm:p-6">
                <div className={`flex gap-3 rounded-xl border p-3 ${
                  isDark ? 'border-sminy-primary/30 bg-sminy-primary/10' : 'border-sminy-primary/20 bg-sminy-primary/5'
                }`}>
                  <Clock className="h-5 w-5 flex-shrink-0 text-sminy-primary" />
                  <p className={`text-xs leading-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Fora destes períodos, os envios aguardam até o próximo horário disponível.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {daysOfWeek.map((day) => {
                    const dayData = schedule[day.key] || { enabled: false, start: '09:00', end: '17:00' };

                    return (
                      <div
                        key={day.key}
                        className={`rounded-2xl border p-4 transition-colors ${
                          dayData.enabled
                            ? isDark
                              ? 'border-sminy-primary/40 bg-sminy-primary/10'
                              : 'border-sminy-primary/30 bg-sminy-primary/5'
                            : isDark
                              ? 'border-gray-700 bg-[#111827]/50'
                              : 'border-gray-200 bg-gray-50/70'
                        }`}
                      >
                        <div className="mb-4 flex items-center justify-between gap-3">
                          <div>
                            <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              {day.label}
                            </p>
                            <p className={`mt-0.5 text-xs ${
                              dayData.enabled ? 'text-sminy-primary' : isDark ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                              {dayData.enabled ? 'Envios ativos' : 'Sem envios'}
                            </p>
                          </div>

                          {isEditingSchedule ? (
                            <label className="relative inline-flex cursor-pointer items-center">
                              <span className="sr-only">{dayData.enabled ? `Desativar ${day.label}` : `Ativar ${day.label}`}</span>
                              <input
                                type="checkbox"
                                checked={dayData.enabled}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setSchedule((prev) => ({
                                    ...prev,
                                    [day.key]: { ...prev[day.key], enabled: checked },
                                  }));
                                }}
                                className="peer sr-only"
                              />
                              <div className={`h-6 w-11 rounded-full transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:after:translate-x-5 ${
                                dayData.enabled ? 'bg-sminy-primary' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                              }`} />
                            </label>
                          ) : (
                            <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                              dayData.enabled
                                ? 'bg-sminy-primary/10 text-sminy-primary'
                                : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                            }`}>
                              {dayData.enabled ? 'Ativo' : 'Inativo'}
                            </span>
                          )}
                        </div>

                        {dayData.enabled ? (
                          isEditingSchedule ? (
                            <div className="grid grid-cols-2 gap-3">
                              {(['start', 'end'] as const).map((field) => (
                                <label key={field} className="space-y-1.5">
                                  <span className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {field === 'start' ? 'Início' : 'Fim'}
                                  </span>
                                  <input
                                    type="time"
                                    value={dayData[field]}
                                    onChange={(event) => {
                                      setSchedule((prev) => ({
                                        ...prev,
                                        [day.key]: { ...prev[day.key], [field]: event.target.value },
                                      }));
                                    }}
                                    className={`w-full rounded-xl border p-2 text-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 ${
                                      isDark
                                        ? 'border-gray-600 bg-gray-700 text-white'
                                        : 'border-gray-300 bg-white text-gray-900'
                                    }`}
                                  />
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${
                              isDark ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700'
                            }`}>
                              <Clock className="h-4 w-4 text-sminy-primary" />
                              <span>{dayData.start}</span>
                              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>até</span>
                              <span>{dayData.end}</span>
                            </div>
                          )
                        ) : (
                          <div className={`rounded-xl border border-dashed px-3 py-3 text-center text-xs ${
                            isDark ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'
                          }`}>
                            Ative o dia para configurar o período
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={`overflow-hidden rounded-2xl border shadow-xl ${
              isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
            }`}>
              <div className={`flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
                isDark ? 'border-gray-700' : 'border-gray-100'
              }`}>
                <div>
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Visão da semana
                  </h3>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Distribuição dos períodos no formato de 24 horas.
                  </p>
                </div>
                <span className="w-fit rounded-full bg-sminy-primary/10 px-2.5 py-1 text-xs font-medium text-sminy-primary">
                  {enabledScheduleDays.length} {enabledScheduleDays.length === 1 ? 'dia ativo' : 'dias ativos'}
                </span>
              </div>

              <div className="p-4 sm:p-6">
                {enabledScheduleDays.length > 0 ? (
                  <div className="space-y-4">
                    <div className={`ml-0 hidden grid-cols-5 text-[10px] sm:grid sm:pl-28 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <span>00h</span>
                      <span className="text-center">06h</span>
                      <span className="text-center">12h</span>
                      <span className="text-center">18h</span>
                      <span className="text-right">24h</span>
                    </div>
                    {enabledScheduleDays.map((day) => {
                      const dayData = schedule[day.key];
                      const range = getScheduleRangePercentages(dayData.start, dayData.end);

                      return (
                        <div key={day.key} className="grid gap-2 sm:grid-cols-[96px_minmax(0,1fr)_112px] sm:items-center sm:gap-4">
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            {day.label}
                          </span>
                          <div className={`relative h-2.5 overflow-hidden rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <div
                              className="absolute top-0 h-full rounded-full bg-sminy-primary"
                              style={{ left: `${range.left}%`, width: `${range.width}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium sm:text-right ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            {dayData.start}–{dayData.end}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className={`mb-3 grid h-12 w-12 place-items-center rounded-2xl ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <Clock className={`h-6 w-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      Nenhum dia ativo
                    </h4>
                    <p className={`mt-1 max-w-sm text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Ative pelo menos um dia para visualizar a distribuição semanal.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {scheduleExists && (
              <div className={`flex flex-col-reverse gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
                isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'
              }`}>
                {scheduleEditMode ? (
                  <button
                    type="button"
                    className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                      isDark
                        ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      if (scheduleBeforeEdit) {
                        setSchedule(scheduleBeforeEdit);
                      }
                      setScheduleBeforeEdit(null);
                      setScheduleEditMode(false);
                    }}
                  >
                    Cancelar edição
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                      isDark
                        ? 'border-red-500/40 text-red-300 hover:bg-red-500/10'
                        : 'border-red-200 text-red-600 hover:bg-red-50'
                    }`}
                    onClick={() => setShowDeleteScheduleModal(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                    Excluir configuração
                  </button>
                )}
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  As mudanças afetam apenas os próximos envios.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showDeleteStepModal && editandoPasso && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Fechar confirmação de exclusão do passo"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDeleteStepModal(false)}
          />
          <div className={`relative z-10 w-full max-w-md overflow-hidden rounded-2xl shadow-xl ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`border-b px-6 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl ${
                  isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-600'
                }`}>
                  <Trash2 className="h-5 w-5" />
                </div>
                <div>
                  <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Excluir passo {editandoPasso.step_number}?
                  </h2>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    O passo e suas mensagens serão removidos do fluxo. Depois, clique em “Salvar fluxo” para confirmar a nova versão.
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex flex-col-reverse gap-3 border-t px-6 py-4 sm:flex-row sm:justify-end ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                type="button"
                className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                  isDark
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setShowDeleteStepModal(false)}
              >
                Manter passo
              </button>
              <button
                type="button"
                className="rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                onClick={excluirPassoEmEdicao}
              >
                Excluir passo
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteScheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Fechar confirmação de exclusão"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDeleteScheduleModal(false)}
          />
          <div className={`relative z-10 w-full max-w-md rounded-2xl shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`border-b px-6 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl ${
                  isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-600'
                }`}>
                  <Trash2 className="h-5 w-5" />
                </div>
                <div>
                  <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Excluir horários de envio?
                  </h2>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    A configuração semanal será removida. Você poderá criar novos horários depois.
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex flex-col-reverse gap-3 px-6 py-4 sm:flex-row sm:justify-end ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                type="button"
                className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                  isDark
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setShowDeleteScheduleModal(false)}
              >
                Manter horários
              </button>
              <button
                type="button"
                className="rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                onClick={async () => {
                  await handleDeleteScheduleConfig();
                  setShowDeleteScheduleModal(false);
                }}
              >
                Excluir horários
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Confirmation
